import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Spinners from "../../components/Common/Spinner";
import NetworkErrorModal from "../../components/Common/NetworkErrorModal";
import SessionExpiredModal from "../../components/Common/SessionExpiredModal";
import { useAuth, useAuthInitialization } from "../../store/redux/api/auth";
import { selectCurrentUser } from "../../store/redux/api/auth/authSlice";
import { consumeReturnUrl, rememberReturnUrl } from "../../api/client";

/**
 * Middleware защиты маршрутов (EyeHub) и «аварийный шелл» приложения.
 *
 * Обязанности:
 *   1. один initializeAuth fetch на startup (через useAuthInitialization);
 *   2. редирект неаутентифицированного на /login с запоминанием `returnUrl`
 *      (запись — только через api/client, чтобы ключ не расползался по коду);
 *   3. возврат на `returnUrl` сразу после успешного входа — раньше значение
 *      писалось в двух местах и не читалось нигде;
 *   4. монтирование глобальных обработчиков ошибок: `window.onCriticalError`
 *      (5xx/офлайн → NetworkErrorModal) и `window.onSessionExpired`
 *      (401 → SessionExpiredModal). До этого оба хука вызывались из
 *      api/client.js и ErrorBoundary, но никем не назначались.
 */
const Authmiddleware = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { isInitialized } = useAuthInitialization();
  const { isAuthenticated, isLoading } = useAuth();
  const user = useSelector(selectCurrentUser);

  const [criticalError, setCriticalError] = useState(null);
  const [expiredSession, setExpiredSession] = useState(null);
  const returnUrlRestored = useRef(false);

  // Глобальные обработчики критических ошибок и истечения сессии.
  useEffect(() => {
    window.onCriticalError = (error) => setCriticalError(error);
    window.onSessionExpired = (payload) => setExpiredSession(payload || {});
    return () => {
      window.onCriticalError = null;
      window.onSessionExpired = null;
    };
  }, []);

  // Глобальный контекст пользователя (совместимость с legacy-кодом шаблона).
  useEffect(() => {
    window.currentUser = isAuthenticated && user ? user : null;
  }, [isAuthenticated, user]);

  // Возврат на сохранённый адрес сразу после входа (однократно за сессию SPA).
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || returnUrlRestored.current) return;
    returnUrlRestored.current = true;
    const target = consumeReturnUrl();
    if (target && target !== location.pathname + location.search) {
      navigate(target, { replace: true });
    }
  }, [isInitialized, isAuthenticated, navigate, location]);

  const handleRelogin = useCallback(() => {
    window.location.replace("/login");
  }, []);

  const handleRetryCritical = useCallback(() => {
    setCriticalError(null);
  }, []);

  // Спиннер пока система инициализируется.
  if (!isInitialized || isLoading) {
    return <Spinners />;
  }

  // Не аутентифицирован → на логин, сохраняя returnUrl (единая точка записи).
  if (!isAuthenticated) {
    rememberReturnUrl(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      {children}
      <SessionExpiredModal
        isOpen={!!expiredSession}
        returnUrl={expiredSession?.returnUrl || ""}
        onRelogin={handleRelogin}
      />
      <NetworkErrorModal
        isOpen={!!criticalError && !expiredSession}
        toggle={handleRetryCritical}
        errorMessage={criticalError?.message}
        onRetry={handleRetryCritical}
      />
    </>
  );
};

export default Authmiddleware;
